import { Link } from 'gatsby'
import { useState } from 'react'
import type { SubmitHandler } from 'react-hook-form'
import { useForm } from 'react-hook-form'
import ButtonSpinner from 'src/components/account/Profile/Tabs/components/addresses/ButtonSpinner'
import { ChevronDown } from 'src/components/Icons/ChevronDown'
import { useToastContext } from 'src/components/ui/ToastMessage/ToastContext'
import { axiosClient } from 'src/services/axiosInstance'
import { makeEventTrack, sendEvent } from 'src/utils/restructure/analytics'

type CampaignFormProps = {
  enableSession: boolean
  formTitle: string
  formSubTitle: string
  campaignName: string
}

type FormData = {
  name: string
  email: string
  phone: string
  sport: string
}

const sportOptions = [
  'Ciclismo',
  'Corrida e Caminhada',
  'Esportes Coletivos (futebol, basquete, vôlei, etc)',
  'Fitness e Musculação',
  'Hipismo',
  'Montanha, Camping e Trilha',
  'Natação, Surf e Moda Praia',
  'Pilates e Yoga',
  'Raquetes',
  'Suplementos e acessórios',
  'Todas as ofertas',
]

const CampaignForm = ({
  campaignName,
  enableSession,
  formSubTitle,
  formTitle,
}: CampaignFormProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const { sendToast } = useToastContext()
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormData>()

  const resetForm = () => {
    setValue('name', '')
    setValue('email', '')
    setValue('phone', '')
    setValue('sport', '')
  }

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    try {
      setIsLoading(true)
      const response = await axiosClient.post(
        '/api/vtex/store/v1/app/campaign-leads',
        { ...data, campaign: campaignName }
      )

      if (response.status === 201 || response.status === 200) {
        sendToast({
          variant: 'success',
          text: 'Cadastro realizado com sucesso!',
        })

        const createAccountSuccessEvent = makeEventTrack({
          eventAction: `generate_lead`,
          eventPage: `${campaignName} - sucesso`,
        })

        sendEvent(createAccountSuccessEvent)

        setIsLoading(false)
        resetForm()

        return
      }

      const error = response.data

      console.error(error)

      const createFailedAccountEvent = makeEventTrack({
        eventAction: `generate_lead`,
        eventPage: `${campaignName} - falhou`,
      })

      sendEvent(createFailedAccountEvent)

      sendToast({
        variant: 'error',
        text: 'Erro ao realizar cadastro, tente novamente mais tarde.',
      })
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      console.error(error)
      sendToast({
        variant: 'error',
        text: 'Erro ao realizar cadastro, tente novamente mais tarde.',
      })
    }
  }

  const handleCellPhoneChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value.replace(/\D/g, '')
    const formattedValue = value
      .replace(/^(\d{2})(\d)/g, '($1) $2')
      .replace(/(\d)(\d{4})$/, '$1-$2')

    setValue('phone', formattedValue)
  }

  if (!enableSession) {
    return null
  }

  return (
    <section className="campaign-form p-4 md:p-20">
      <div className="bg-restructure-background-neutral-3 rounded-2xl md:rounded-[56px] flex justify-center items-center max-w-[1280px] mx-auto py-8 px-4 md:py-16 md:px-8">
        <div className="max-w-[628px] w-full flex flex-col">
          <div className="font-inter text-center flex flex-col gap-4 md:gap-6">
            <h3 className="text-2xl font-medium md:font-normal md:text-[2.5rem] text-restructure-primary leading-8 md:leading-[48px]">
              {formTitle}
            </h3>
            <p className="text-sm md:text-lg md:text-secondary font-normal">
              {formSubTitle}
            </p>
          </div>

          <form
            className="flex flex-col mt-8 md:mt-14 gap-4 md:gap-6 font-inter text-base text-black"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
          >
            <div>
              <input
                {...register('name', {
                  required: true,
                  validate: (value) => value.trim() !== '',
                })}
                type="text"
                placeholder="Nome completo *"
                className="w-full h-16 px-4 border hover:border-[2px] border-neutral01 focus:outline-none focus:border-restructure-background-blue placeholder:text-restructure-disabled"
              />
              {errors.name && (
                <span className="text-xs text-restructure-error mt-2">
                  Por favor, preencha este campo obrigatório.
                </span>
              )}
            </div>

            <div>
              <input
                {...register('email', { required: true })}
                type="email"
                placeholder="E-mail *"
                className="w-full h-16 px-4 border hover:border-[2px] border-neutral01 focus:outline-none focus:border-restructure-background-blue placeholder:text-restructure-disabled"
              />
              {errors.email && (
                <span className="text-xs text-restructure-error mt-2">
                  Por favor, preencha este campo obrigatório.
                </span>
              )}
            </div>

            <div>
              <input
                {...register('phone', {
                  required: true,
                  minLength: 14,
                  maxLength: 15,
                })}
                type="text"
                max={16}
                onChange={handleCellPhoneChange}
                placeholder="Celular com DDD *"
                className="w-full h-16 px-4 border hover:border-[2px] border-neutral01 focus:outline-none focus:border-restructure-background-blue placeholder:text-restructure-disabled"
              />
              {errors.phone && (
                <span className="text-xs text-restructure-error mt-2">
                  {errors.phone.type === 'required'
                    ? 'Por favor, preencha este campo obrigatório.'
                    : 'Por favor, insira um número de celular válido.'}
                </span>
              )}
            </div>

            <div className="relative">
              <select
                {...register('sport')}
                className="has-[option:disabled:checked]:text-restructure-disabled appearance-none w-full h-16 px-4 border hover:border-[2px] border-neutral01 focus:outline-none focus:border-restructure-background-blue"
                defaultValue=""
              >
                <option value="" disabled selected>
                  Selecione seu esporte de interesse
                </option>
                {sportOptions.map((value, i) => (
                  <option key={i} value={value}>
                    {value}
                  </option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <ChevronDown />
              </div>
            </div>

            <div className="flex flex-wrap justify-center text-restructure-disabled md:-mt-2">
              <span>
                Ao enviar estas informações, você concorda com os{' '}
                <Link
                  to="/servicos/politica-de-privacidade"
                  target="_blank"
                  className="underline underline-offset-2"
                >
                  termos e condições
                </Link>
              </span>
            </div>
            <button
              className="flex items-center justify-center mt-6 md:mt-4 w-full h-16 bg-restructure-background-blue rounded-round text-lg font-semibold text-white hover:bg-restructure-border-action-hover"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? <ButtonSpinner /> : 'Enviar'}
            </button>
          </form>
        </div>
      </div>
    </section>
  )
}

export default CampaignForm
